import { useCallback, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { ThumbsUpIcon } from 'foundations-assets/icons/components/ThumbsUpIcon';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { ReviewRatingInput, ReviewTextInput } from 'trellis-ecosystem';
import { EcosystemReviewPopover } from 'marketplace-ui-reviews/components/wizard/EcosystemReviewPopover';
import { useReviewability } from 'marketplace-ui-reviews/hooks/useReviewability';
import { getReviewTypeFromProductType } from 'marketplace-ui-reviews/utils/getReviewTypeFromProductType';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { DetailsPageCard } from './DetailsPageCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function ProductFeedbackCard({
  partnerPortalId,
  logInUrl
}) {
  const {
    appContext,
    listingId,
    productType,
    trackInteraction
  } = useDetailsPageContext();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const {
    unreviewableReason,
    requestState
  } = useReviewability({
    entityId: listingId,
    entityPortalId: partnerPortalId,
    reviewType: getReviewTypeFromProductType(productType),
    isPublicApp: appContext === 'public',
    reviewPending: reviewSubmitted
  });
  const handleTrackInteraction = useCallback(eventProps => {
    trackInteraction(Object.assign({
      section: 'product feedback card'
    }, eventProps));
  }, [trackInteraction]);
  if (appContext === 'public') {
    return /*#__PURE__*/_jsxs(DetailsPageCard, {
      id: "details-page-feedback-public",
      name: "feedback card",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.feedbackCard.title"
      }),
      children: [/*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.feedbackCard.subtitle"
        })
      }), /*#__PURE__*/_jsx(Button, {
        href: logInUrl,
        use: "tertiary-light",
        children: /*#__PURE__*/_jsx(MediaObject, {
          itemLeft: /*#__PURE__*/_jsx(ThumbsUpIcon, {}),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.feedbackCard.buttonTextPublic"
          })
        })
      })]
    });
  }
  return /*#__PURE__*/_jsxs(DetailsPageCard, {
    id: "details-page-feedback",
    name: "feedback card",
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.feedbackCard.title"
    }),
    children: [/*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.feedbackCard.subtitle"
      })
    }), /*#__PURE__*/_jsx(EcosystemReviewPopover, {
      bodyContent: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ReviewRatingInput, {
          fieldKey: "OVERALL",
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.feedbackCard.ratingLabel"
          }),
          showTextRating: false,
          size: "800"
        }), /*#__PURE__*/_jsx(ReviewTextInput, {
          fieldKey: "REVIEW",
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.feedbackCard.reviewLabel"
          }),
          placeholder: unescapedText('detailsPage.feedbackCard.reviewPlaceholder')
        })]
      }),
      errorAlertTitle: unescapedText('detailsPage.feedbackCard.errorAlertTitle'),
      headerContent: /*#__PURE__*/_jsx(Text, {
        as: "h4",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.feedbackCard.popoverHeader"
        })
      }),
      offeringId: listingId,
      onClose: () => {
        handleTrackInteraction({
          action: 'close feedback popover'
        });
        setPopoverOpen(false);
      },
      onSuccess: () => {
        handleTrackInteraction({
          action: 'submit feedback'
        });
        setReviewSubmitted(true);
        setPopoverOpen(false);
      },
      popoverProps: {
        inlineSize: '400px',
        open: popoverOpen
      },
      reviewType: getReviewTypeFromProductType(productType),
      submitButtonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.feedbackCard.submitButton"
      }),
      successAlertMessage: null,
      successAlertTitle: unescapedText('detailsPage.feedbackCard.successAlertTitle'),
      validationSchema: {
        ratings: {
          OVERALL: {
            required: true
          }
        }
      },
      children: /*#__PURE__*/_jsx(LoadingWrapper, {
        requestStatus: requestState,
        children: /*#__PURE__*/_jsx(Tooltip, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `marketplace-ui-reviews.error.${unreviewableReason}`
          }),
          disabled: !unreviewableReason,
          children: /*#__PURE__*/_jsx(Button, {
            disabled: !!unreviewableReason,
            onClick: () => {
              setPopoverOpen(true);
              handleTrackInteraction({
                action: 'open feedback popover'
              });
            },
            use: "tertiary-light",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.feedbackCard.buttonText"
            })
          })
        })
      })
    })]
  });
}